<template>
  <page-wrapper :title="title" :has-pad="true">
    <!-- Loading Section -->
    <LoadingOverlay :active="isLoading" />

    <div class="d-flex justify-content-between">
      <div class="d-flex">
        <div class="mr-4">
          <sc-button
            :disabled="isViewOnlyByRole"
            variant="secondary"
            @click="handleClickDuplicate"
            >สร้างแพ็กเกจโดยอ้างอิงจากแพ็กเกจนี้</sc-button
          >
          <sc-button variant="secondary" @click="handleClickDownloadReport"
            >Download Report</sc-button
          >
          <sc-button
            v-if="isShowExportRegistrationFormButton"
            variant="secondary"
            @click="handleClickExportRegistrationFormButton"
            >Export ข้อมูล Registration Form</sc-button
          >
        </div>
        <language-select
          v-model="language"
          @change="handleChangeLanguage"
        ></language-select>
      </div>

      <div>
        <sc-button
          v-if="!isViewOnlyByRole && isEditing"
          variant="secondary"
          @click="isEditing = false"
          >ยกเลิก</sc-button
        >
        <sc-button
          v-if="!isViewOnlyByRole && !isEditing"
          variant="primary"
          @click="isEditing = true"
          >แก้ไข</sc-button
        >
      </div>
    </div>
    <div class="wrapper">
      <package-status-card
        class="mb-4"
        :isAvailableForSale.sync="isAvailableForSale"
        :isVisibleOnApp.sync="isVisibleOnApp"
        :disabled="isFormDisabled"
      ></package-status-card>
      <package-editor
        ref="packageEditor"
        v-model="form"
        :image-url="imageUrl"
        :thumbnail-url="thumbnailUrl"
        :thumbnail-ad-url="thumbnailAdUrl"
        :targets="targets"
        :seller-trade-parties="sellerTradeParties"
        :package-groups="packageGroups"
        :collections="collections"
        :payment-campaigns="paymentCampaigns"
        :disabled="isFormDisabled"
        :is-disabled-by-language-not-t-h="isDisabledByLanguageNotTH"
        :is-disabled-by-edit-package="true"
        :language="language"
        @selectedService="handleSetupPricings"
      ></package-editor>
      <div
        v-if="!isViewOnlyByRole && isEditing"
        class="d-flex justify-content-between mt-4"
      >
        <sc-button variant="danger" @click="handleClickRemove">ลบ</sc-button>
        <sc-button variant="primary" @click="handleClickSave">บันทึก</sc-button>
      </div>
    </div>
    <confirm-dialog
      v-show="exportDialog.isShow"
      :data="exportDialog.data"
      name="exportModal"
      @confirm="handleConfirm"
    >
      <div class="mb-4 text-left" style="width: 350px; max-width: 100%">
        <label class="label">ประเภท</label>
        <el-select v-model="exportType" class="w-100">
          <el-option value="xlsx" label="Microsoft Excel (.xlsx)"></el-option>
          <el-option value="zip" label="PDF (.zip)"></el-option>
        </el-select>
      </div>
      <div>
        <div class="text-start">
          <label class="label">ช่วงเวลา</label>
        </div>
        <date-picker v-model="exportForm" type="daterange"></date-picker>
      </div>
    </confirm-dialog>
  </page-wrapper>
</template>

<script>
import { Select as ElSelect, Option as ElOption, MessageBox } from "element-ui";
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import LanguageSelect from "@/lib/components/LanguageSelect";
import { baanRueJaiClient, backendAdminClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import { toCamelCaseObject, existValueAndEmptyString } from "@/lib/util";
import { localization } from "@/lib/mapper";
import DatePicker from "@/lib/components/DatePicker";
import { isNil, isEmpty, head, tail } from "ramda";
import { toOptionalDateTime, toOptionalISOString } from "@/lib/dateTime";
import {
  convertStangToBathAndRoundTwoDecimal,
  convertBathToStangAndRoundTwoDecimal,
  distributePrice,
} from "@/lib/formatter";
import LoadingOverlay from "@/lib/components/LoadingOverlay";
import PackageStatusCard from "../components/PackageStatusCard";
import PackageEditor from "../components/PackageEditor";
import { findEmptyAppointmentPriceRow } from "../models/PackageOptionPrice";

export default {
  components: {
    PageWrapper,
    ScButton,
    LanguageSelect,
    PackageEditor,
    PackageStatusCard,
    DatePicker,
    ElSelect,
    ElOption,
    LoadingOverlay,
  },
  data() {
    const title = "รายละเอียดแพ็กเกจ";
    const form = {
      id: null,
      name: "",
      receiptName: "",
      target: "สำหรับบ้าน",
      price: 0,
      pricings: [],
      billingCycle: 1,
      detail: "",
      termsAndConditions: "",
      sellerTradePartyId: null,
      services: [],
      projects: [],
      homes: [],
      imageFile: null,
      thumbnailFile: null,
      thumbnailAdFile: null,
      is_secret: false,
      is_expire_after_appointment: false,
      noteLabel: null,
      originalPrice: null,
      packageCollection: null,
      purchaseSuccessTitle: null,
      purchaseSuccessMessage: null,
      expiredAt: null,
      isReadonlyPackage: true,
      maximumQuantity: 0,
      minimumQuantity: 0,
      packageOptions: null,
      packageAlbums: [],
      coinAmountPercent: null,
      coinAmountFix: null,
      keywords: null,
      paymentCampaignIds: [],
      isRequiredIssueReceipt: true,
      detailImagesPosition: "TOP",
      detailImageAlbums: [],
    };

    const imageUrl = null;
    const targets = [];
    const thumbnailUrl = null;
    const thumbnailAdUrl = null;
    const sellerTradeParties = [];
    const paymentCampaigns = [];
    const packageGroups = [];
    const collections = [];

    const permission =
      JSON.parse(localStorage.getItem("adminRolePermission")) || null;

    return {
      title,
      form,
      isAvailableForSale: false,
      imageUrl,
      targets,
      thumbnailUrl,
      thumbnailAdUrl,
      sellerTradeParties,
      paymentCampaigns,
      packageGroups,
      collections,
      exportDialog: {
        isShow: false,
        data: {
          title: "Export Registration Form",
          buttonCancel: "ยกเลิก",
          buttonSubmit: "ยืนยัน",
        },
      },
      exportForm: [],
      exportType: "xlsx", // xlsx | zip
      language: localization.TH,
      newOptionVariantPrices: [],
      newServicePrices: [],
      permission,
      isVisibleOnApp: false,
      isLoading: true,
      isEditing: false,
    };
  },
  computed: {
    isEditorDisabled() {
      return this.$isCallCenter;
    },
    isDisabledByLanguageNotTH() {
      return this.language !== localization.TH;
    },
    isFormDisabled() {
      return this.isViewOnlyByRole || !this.isEditing;
    },
    exportQuery() {
      const [from, to] = this.exportForm;

      return {
        start_at: from.toISO(),
        end_at: to.toISO(),
      };
    },
    isShowExportRegistrationFormButton() {
      return this.form.registrationForm;
    },
    isViewOnlyByRole() {
      const roles = ["AC", "CC"];
      return roles.includes(this.permission.role);
    },
  },
  watch: {
    form(form, oldForm) {
      if (this.form.imageFile !== oldForm.imageFile) {
        this.imageUrl = URL.createObjectURL(this.form.imageFile);
      }

      if (this.form.thumbnailAdFile !== oldForm.thumbnailAdFile) {
        this.thumbnailAdUrl = this.form.thumbnailAdFile
          ? URL.createObjectURL(this.form.thumbnailAdFile)
          : null;
      }

      if (this.form.thumbnailFile !== oldForm.thumbnailFile) {
        this.thumbnailUrl = URL.createObjectURL(this.form.thumbnailFile);
      }

      if (!isNil(this.form.registrationForm)) {
        this.form.registrationForm.sections.forEach((_section, si) => {
          const section = this.form.registrationForm.sections[si];
          section.inputs.forEach((_input, ii) => {
            const formInput = section.inputs[ii];
            formInput.watermarkUrl = !isNil(formInput.watermarkFile)
              ? URL.createObjectURL(formInput.watermarkFile)
              : formInput.watermarkUrl;
          });
        });
      }
    },
  },
  created() {
    this.setup();
  },
  methods: {
    getRoutePackageId() {
      const { id } = this.$route.params;
      if (!id) {
        this.$message.error("ไม่พบรหัสแพ็กเกจ");
        return null;
      }

      return id;
    },
    setup() {
      this.setupData();
    },
    setupData() {
      this.setupTargets();
      this.setupSellerTradeParties();
      this.setupPaymentCampaigns();
      this.setupPackageGroups();
      this.setupCollections();
      this.setupForm();
    },
    async setupTargets() {
      const targets = await this.fetchTargets();

      this.targets = targets;
    },
    async setupSellerTradeParties() {
      const sellerTradeParties = await this.fetchSellerTradeParties();

      this.sellerTradeParties = sellerTradeParties;
    },
    async setupPackageGroups() {
      const packageGroups = await this.fetchPackageGroups();

      this.packageGroups = packageGroups;
    },
    async setupCollections() {
      const collections = await this.fetchList();

      this.collections = collections;
    },
    async setupPaymentCampaigns() {
      const paymentCampaigns = await this.fetchPaymentCampaigns();

      this.paymentCampaigns = paymentCampaigns;
    },
    async setupForm() {
      const data = await this.fetchData();
      if (!data) {
        return;
      }

      const {
        id,
        name,
        receipt_name: receiptName,
        detail,
        target,
        price,
        pricings,
        billing_cycle: billingCycle,
        terms_and_conditions: termsAndConditions,
        seller_trade_party_id: sellerTradePartyId,
        image_url: imageUrl,
        services,
        homes,
        project_ids: projectIds,
        thumbnail_url: thumbnailUrl,
        is_secret: isSecret,
        is_expire_after_appointment: isExpireAfterAppointment,
        note_label: noteLabel,
        registration_form: registrationForm,
        subpackages,
        superpackages,
        purchasing_limit: purchasingLimit,
        original_price: originalPrice,
        purchase_success_title: purchaseSuccessTitle,
        purchase_success_message: purchaseSuccessMessage,
        expired_at: expiredAt,
        maximum_quantity: maximumQuantity,
        minimum_quantity: minimumQuantity,
        package_options: packageOptions,
        package_albums: packageAlbums,
        coin_amount_percent: coinAmountPercent,
        coin_amount_fix: coinAmountFix,
        tags: packageTags,
        advance_notice_duration: advanceNoticeDuration,
        advance_notice_message: advanceNoticeMessage,
        is_ignore_count_coupon: isIgnoreCountCoupon,
        payment_campaign_ids: paymentCampaignIds,
        is_required_issue_receipt: isRequiredIssueReceipt,
        thumbnail_ad: thumbnailAdUrl,
        detail_images: detailImageAlbums,
        detail_images_position: detailImagesPosition,
        label_ids: labelIds,
        package_groups: packageGroupIds,
        collection_ids: collectionIds,
        is_available_for_sale: isAvailableForSale,
        is_visible_on_app: isVisibleOnApp,
      } = data;

      const newPackageOptions = await this.setupPackageOptionsDividePrice(
        packageOptions
      );
      const hasAdvanceNotice = !!advanceNoticeDuration;

      let coinAmountStatus = "no";
      if (coinAmountPercent) {
        coinAmountStatus = "percent";
      } else if (coinAmountFix) {
        coinAmountStatus = "fix";
      }

      this.form = {
        id,
        name,
        receiptName,
        detail,
        target,
        price: convertBathToStangAndRoundTwoDecimal(price),
        pricings: toCamelCaseObject(pricings),
        originalPrice:
          originalPrice !== null
            ? convertBathToStangAndRoundTwoDecimal(originalPrice)
            : null,
        billingCycle,
        termsAndConditions,
        sellerTradePartyId,
        services: services.map((service) => ({
          ...service,
          iconUrl: service.icon_url,
          provider: {
            ...service.provider,
            logoUrl: service.provider.logo_url,
          },
        })),
        projects: (projectIds || []).map((projectId) => ({ projectId })),
        homes: homes || [],
        packageSelectorType:
          (projectIds || []).length === 0 && (homes || []).length > 0
            ? "perUnit"
            : "project",
        imageFile: null,
        thumbnailFile: null,
        thumbnailAdFile: null,
        detailImagesPosition: detailImagesPosition || "TOP",
        isSecret,
        isExpireAfterAppointment,
        noteLabel,
        registrationForm: toCamelCaseObject(registrationForm),
        superpackages: !isEmpty(superpackages || [])
          ? superpackages.map((item) => item.id)
          : null,
        subpackages: !isEmpty(subpackages || [])
          ? subpackages.map((item) => item.id)
          : null,
        packageCollection: !isEmpty(subpackages || [])
          ? subpackages.map((item) => item.id).toString()
          : null,
        hasOptionPackage: !isEmpty(pricings),
        purchasingLimit,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt: toOptionalDateTime(expiredAt),
        maximumQuantity,
        minimumQuantity: minimumQuantity !== null ? minimumQuantity : 1,
        packageOptions: newPackageOptions,
        isReadonlyPackage: true,
        newOptionVariantPrices: this.newOptionVariantPrices,
        newServicePrices: this.newServicePrices,
        isReadOnlyOptionPrice: false,
        packageAlbums,
        coinAmountPercent,
        coinAmountFix,
        coinAmountStatus,
        keywords: packageTags,
        hasAdvanceNotice,
        advanceNoticeDuration,
        advanceNoticeMessage,
        isIgnoreCountCoupon,
        paymentCampaignIds,
        isRequiredIssueReceipt,
        labelIds: labelIds || [],
        detailImageAlbums,
        packageGroupIds: packageGroupIds || [],
        collectionIds: collectionIds || [],
      };
      this.imageUrl = imageUrl;
      this.thumbnailUrl = thumbnailUrl;
      this.thumbnailAdUrl = thumbnailAdUrl;
      this.isAvailableForSale = isNil(isAvailableForSale)
        ? false
        : isAvailableForSale;
      this.isVisibleOnApp = isNil(isVisibleOnApp) ? false : isVisibleOnApp;
    },
    async fetchData() {
      const id = this.getRoutePackageId();
      if (!id) {
        this.isLoading = false;
        return null;
      }

      const response =
        this.language === localization.TH
          ? await backendAdminClient.subscriptions.getPackageById(id)
          : await backendAdminClient.subscriptions.getPackageById(
              id,
              this.language
            );

      this.isLoading = false;
      return response.data.result;
    },
    getOptions(services) {
      const options = !isEmpty(services)
        ? services.flatMap((service) =>
            service.options.map((option) =>
              option.variants.map((variant) => ({
                service: service.name,
                option: option.title,
                variant: variant.title,
                isSale: variant.is_sale,
              }))
            )
          )
        : [];

      return options;
    },
    fetchTargets() {
      return Promise.resolve([
        { id: "0", name: "สำหรับบ้าน" },
        { id: "1", name: "สำหรับคอนโด" },
        { id: "2", name: "สำหรับบ้านขนาดใหญ่" },
      ]);
    },
    async fetchSellerTradeParties() {
      const response =
        await baanRueJaiClient.admin.subscriptions.receipts.getSellerTradeParties();

      return response.data.result;
    },
    async fetchPackageGroups() {
      return baanRueJaiClient.admin.groups.packageGroups.list();
    },
    async fetchList() {
      const response =
        await baanRueJaiClient.admin.subscriptions.collections.list();

      return response.data.result;
    },
    async fetchPaymentCampaigns() {
      const response =
        await baanRueJaiClient.admin.payment.getPaymentCampaigns();

      return response.data.result;
    },
    setupPricings(options) {
      const pricingPatterns = this.createPricingPatterns(options);

      this.form.pricings = !isEmpty(pricingPatterns)
        ? pricingPatterns.map((pattern) => ({
            variants: pattern,
            isSell: true,
            isSale: false,
            originalPrice: null,
            price: 0,
          }))
        : [];
    },
    createPricingPatterns(options) {
      if (isEmpty(options)) {
        return [];
      }

      if (options.length === 1) {
        return head(options).map((subOption) => [subOption]);
      }

      const firstOption = head(options);
      const otherOptions = tail(options);
      const patternOptions = this.createPricingPatterns(
        otherOptions
      ).flatMap(subOtherOption =>
        firstOption.map(subOption => [subOption, ...subOtherOption])
      );

      return patternOptions;
    },
    async handleClickSave() {
      try {
        await this.$refs.packageEditor.validate();
      } catch (invalidFields) {
        const messages = Object.values(invalidFields || {})
          .map((fieldErrors) => fieldErrors[0] && fieldErrors[0].message)
          .filter(Boolean);

        await this.$alert(
          messages.length
            ? messages.join("<br>")
            : "โปรดตรวจสอบข้อมูลให้ครบถ้วน",
          "แจ้งเตือน",
          {
            confirmButtonText: "ปิด",
            type: "warning",
            dangerouslyUseHTMLString: true,
          }
        );
        return;
      }

      const emptyAppointmentPriceRow = findEmptyAppointmentPriceRow(
        this.form.packageOptions
      );

      if (emptyAppointmentPriceRow) {
        this.$message.error(
          `กรุณาใส่ราคาขายต่อครั้งให้ครบ: ${emptyAppointmentPriceRow}`
        );
        return;
      }

      try {
        if (!this.form.price) {
          this.$message.error("กรุณาใส่ราคา แพ็กเกจ");
          return;
        }

        if (
          this.form.coinAmountPercent &&
          (this.form.coinAmountPercent < 0 || this.form.coinAmountPercent > 100)
        ) {
          this.$message.error("กรุณาแก้ไขเปอร์เซ็นต์ MNC");
          return;
        }

        if (
          this.form.coinAmountFix &&
          (this.form.coinAmountFix < 0 ||
            convertBathToStangAndRoundTwoDecimal(this.form.coinAmountFix) >
              this.form.price)
        ) {
          this.$message.error("กรุณาแก้ไขการใช้ส่วนลด MNC");
          return;
        }

        if (this.form.hasAdvanceNotice === true) {
          if (
            this.form.advanceNoticeDuration > 0 &&
            (isEmpty(this.form.advanceNoticeMessage) ||
              isNil(this.form.advanceNoticeMessage))
          ) {
            this.$message.error("กรุณาใส่ข้อความแจ้งเตือน");
            return;
          }

          if (this.form.advanceNoticeDuration < 1) {
            this.$message.error("กรุณาใส่จำนวนวันแจ้งเตือนล่วงหน้า");
            return;
          }
        }

        if (!this.form.packageAlbums || this.form.packageAlbums.length <= 0) {
          this.$message.error("กรุณาใส่รูปรายละเอียด Package");
          return;
        }

        if (!this.form.imageFile && isNil(this.imageUrl)) {
          this.$message.error("กรุณาใส่รูป RJ Recommended");
          return;
        }

        if (!this.form.thumbnailFile && isNil(this.thumbnailUrl)) {
          this.$message.error("กรุณาใส่รูป Thumbnail");
          return;
        }

        this.isLoading = true;
        await this.updatePackage();

        window.location.reload();
      } catch (err) {
        if (
          err.response &&
          err.response.status &&
          err.response.status === 400
        ) {
          this.$message.error(err.response.data.message);
        } else {
          this.$message.error(err.message);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async updatePackage() {
      const id = this.getRoutePackageId();
      if (!id) {
        return undefined;
      }
      const {
        name,
        receiptName,
        detail,
        target,
        pricings,
        billingCycle,
        termsAndConditions,
        sellerTradePartyId,
        imageFile,
        thumbnailFile,
        services,
        projects,
        homes,
        isSecret,
        isExpireAfterAppointment,
        noteLabel,
        registrationForm,
        packageCollection,
        purchasingLimit,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt,
        minimumQuantity,
        maximumQuantity,
        packageOptions,
        originalPrice,
        price,
        packageAlbums,
        coinAmountPercent,
        coinAmountFix,
        keywords,
        advanceNoticeDuration,
        advanceNoticeMessage,
        isRequiredIssueReceipt,
        paymentCampaignIds,
        thumbnailAdFile,
        detailImageAlbums,
        detailImagesPosition,
        labelIds,
        packageGroupIds,
        collectionIds,
      } = this.form;

      const isOneTimePackage = billingCycle === 0;

      const newPackageOptions = await this.setupPackageOptionsMultiplyPrice(
        packageOptions
      );
      const serviceIds = (services || []).map(({ id: serviceId }) =>
        serviceId.toString()
      );
      const projectIds = (projects || []).map(({ projectId }) => projectId);
      const homeIds = (homes || []).map(({ home_id: homeId }) => homeId);
      const subpackageIds = packageCollection
        ? packageCollection.split(",").map((subpackageId) => subpackageId.trim())
        : [];
      const registrationFormPayload = registrationForm
        ? {
            ...registrationForm,
            sections: await Promise.all(
              registrationForm.sections.map(async (section) => ({
                ...section,
                inputs: await Promise.all(
                  section.inputs.map(async (input) => ({
                    isRequired: input.isRequired,
                    prefillKey: input.prefillKey,
                    title: input.title,
                    type: input.type,
                    watermarkUrl: await this.resolveWatermarkUrl(input, name),
                  }))
                ),
              }))
            ),
          }
        : null;

      const params = {
        name,
        receiptName,
        detail,
        target,
        pricings,
        billingCycle: isOneTimePackage ? 0 : billingCycle,
        termsAndConditions,
        sellerTradePartyId,
        serviceIds,
        projectIds,
        homeIds,
        isSecret,
        isExpireAfterAppointment,
        noteLabel,
        registrationForm: registrationFormPayload,
        subpackageIds,
        purchasingLimit,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt: toOptionalISOString(expiredAt),
        minimumQuantity: parseInt(minimumQuantity, 10),
        maximumQuantity: parseInt(maximumQuantity, 10),
        packageOptions: newPackageOptions,
        originalPrice:
          originalPrice !== null
            ? convertStangToBathAndRoundTwoDecimal(originalPrice)
            : null,
        packageAlbums,
        price: convertStangToBathAndRoundTwoDecimal(price),
        coinAmountPercent,
        coinAmountFix,
        tags: keywords,
        advanceNoticeDuration: parseInt(advanceNoticeDuration, 10),
        advanceNoticeMessage,
        isRequiredIssueReceipt,
        paymentCampaignIds,
        thumbnailAd: this.thumbnailAdUrl,
        detailImages: !isEmpty(detailImageAlbums) ? detailImageAlbums : null,
        detailImagesPosition,
        labelIds,
        packageGroups: packageGroupIds,
        collectionIds,
        isAvailableForSale: this.isAvailableForSale,
        isVisibleOnApp: this.isVisibleOnApp,
      };

      if (imageFile) {
        params.imageUrl = await uploadPublicImage(imageFile, name, "article");
      }

      if (thumbnailFile) {
        params.thumbnailUrl = await uploadPublicImage(
          thumbnailFile,
          name,
          "article"
        );
      }

      if (thumbnailAdFile) {
        params.thumbnailAd = await uploadPublicImage(
          thumbnailAdFile,
          name,
          "article"
        );
      }

      const response =
        this.language === localization.TH
          ? await backendAdminClient.subscriptions.updatePackage(id, params)
          : await backendAdminClient.subscriptions.updatePackage(
              id,
              params,
              this.language
            );

      return response.data.result;
    },
    async handleClickRemove() {
      try {
        await MessageBox.confirm("ยืนยันการลบบริการ", { confirmButtonText: "ตกลง", cancelButtonText: "ยกเลิก" });
      } catch { return; }

      try {
        await this.removePackage();
        this.$router.push({ name: "PackageListPage" });
      } catch (_err) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
      }
    },
    async removePackage() {
      const id = this.getRoutePackageId();
      if (!id) {
        return;
      }

      await baanRueJaiClient.admin.subscriptions.recommend_packages.delete(id);
    },
    handleClickDuplicate() {
      const id = this.getRoutePackageId();
      if (!id) {
        return;
      }

      this.$router.push({ name: "PackageCreatePage", query: { id } });
    },
    handleClickExportRegistrationFormButton() {
      this.exportDialog.isShow = true;
    },
    async handleChangeLanguage() {
      const translationPackage = await this.setupLanguage();

      if (!translationPackage) return;

      this.form = {
        ...this.form,
        target: translationPackage.target,
        noteLabel: translationPackage.note_label,
        purchaseSuccessTitle: translationPackage.purchase_success_title,
        purchaseSuccessMessage: translationPackage.purchase_success_message,
        name: translationPackage.name,
        detail: translationPackage.detail,
        termsAndConditions: translationPackage.terms_and_conditions,
        registrationForm: toCamelCaseObject(
          translationPackage.registration_form
        ),
        language: this.language,
      };
    },
    async setupLanguage() {
      const id = this.getRoutePackageId();
      if (!id) {
        return undefined;
      }

      try {
        const response =
          await baanRueJaiClient.admin.subscriptions.recommend_packages.getLanguage(
            id,
            this.language
          );

        return response.data.result;
      } catch (_error) {
        this.$message.error("ไม่สามารถเปลี่ยนภาษาได้");
        return undefined;
      }
    },
    handleSetupPricings() {
      if (this.form.hasOptionPackage && !isEmpty(this.form.services)) {
        const options = this.getOptions(this.form.services);

        this.setupPricings(options);
      } else {
        this.form.pricings = [];
      }
    },
    async handleConfirm(confirm) {
      if (!confirm) {
        this.exportDialog.isShow = false;
        return;
      }

      if (this.exportType === "xlsx") {
        await this.downloadExcel();
      } else { await this.downloadZip(); }

      this.exportDialog.isShow = false;
      this.exportForm = [];
      this.exportType = "xlsx";
    },
    async downloadExcel() {
      const [from, to] = this.exportForm;

      if (!from || !to) {
        return;
      }

      const id = this.getRoutePackageId();
      if (!id) {
        return;
      }

      const response =
        await baanRueJaiClient.admin.subscriptions.recommend_packages.getRegistrationFormsReport(
          id,
          this.exportQuery
        );

      const filename = `${this.form.name}${from.toFormat(
        "d/M/yyyy"
      )}-${to.toFormat("d/M/yyyy")}.xlsx`;
      const blob = new Blob([response.data]);

      this.downloadAsFile(filename, blob);
    },
    async downloadZip() {
      const [from, to] = this.exportForm;

      if (!from || !to) {
        return;
      }

      const id = this.getRoutePackageId();
      if (!id) {
        return;
      }

      const response =
        await baanRueJaiClient.admin.subscriptions.subscribers.getPackageRegistrationFormsPdfsZip(
          id
        );

      const filename = `${this.form.name}${from.toFormat(
        "d/M/yyyy"
      )}-${to.toFormat("d/M/yyyy")}.zip`;
      const blob = new Blob([response.data]);

      this.downloadAsFile(filename, blob);
    },
    downloadAsFile(filename, blob) {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    },
    async handleClickDownloadReport() {
      const id = this.getRoutePackageId();
      if (!id) {
        return;
      }

      try {
        const response =
          await baanRueJaiClient.admin.subscriptions.recommend_packages.getReport(
            id
          );

        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `report-active-subscribers-${id}.xlsx`;
        link.click();
      } catch (error) {
        // TODO: use alert message from response message backend
        this.$message.error(
          "ไม่สามารถ Download Report ได้ เนื่องจากไม่มีข้อมูลหรือเกิดข้อผิดพลาด"
        );
        console.error(error);
      }
    },
    async setupPackageOptionsDividePrice(packageOptionsParam) {
      if (!packageOptionsParam) return undefined;

      for (let pi = 0; pi < packageOptionsParam.length; pi += 1) {
        const packageOption = packageOptionsParam[pi];
        const serviceOriginalPrice = convertBathToStangAndRoundTwoDecimal(
          packageOption.service_original_price
        );
        const servicePrice = convertBathToStangAndRoundTwoDecimal(
          packageOption.service_price
        );

        packageOption.service_original_price = serviceOriginalPrice;
        packageOption.service_price = servicePrice;

        if (
          packageOption.service_appointment_prices &&
          packageOption.service_appointment_prices.length > 0
        ) {
          for (
            let ai = 0;
            ai < packageOption.service_appointment_prices.length;
            ai += 1
          ) {
            const ap = packageOption.service_appointment_prices[ai];
            ap.appoint_price = convertBathToStangAndRoundTwoDecimal(
              ap.appoint_price
            );
          }
        }

        if (
          servicePrice === undefined &&
          !packageOption.service_option_variants
        ) {
          this.newServicePrices.push(packageOption.service_id);
        }

        // An out-of-stock row has its price fields locked, so a discount flag left on it
        // can never be corrected by hand. Undo it exactly as unticking ลดราคา would —
        // drop the flag and put the normal price back — and let the next save persist it.
        // A row with no normal price has nothing to copy back and is left for data cleanup.
        if (packageOption.is_out_of_stock && packageOption.is_discount) {
          packageOption.is_discount = false;

          if (existValueAndEmptyString(packageOption.service_original_price)) {
            packageOption.service_price = packageOption.service_original_price;
            distributePrice(
              packageOption.service_appointment_prices,
              packageOption.service_original_price
            );
          }
        }

        if (packageOption.service_option_variants) {
          for (
            let soi = 0;
            soi < packageOption.service_option_variants.length;
            soi += 1
          ) {
            const serviceOptionVariant =
              packageOption.service_option_variants[soi];
            for (
              let vi = 0;
              vi < serviceOptionVariant.variants.length;
              vi += 1
            ) {
              const variant = serviceOptionVariant.variants[vi];
              const variantPrice = convertBathToStangAndRoundTwoDecimal(
                variant.price
              );
              const variantOriginalPrice = convertBathToStangAndRoundTwoDecimal(
                variant.original_price
              );
              variant.price = variantPrice;
              variant.original_price = variantOriginalPrice;

              if (variantPrice === undefined) {
                this.newOptionVariantPrices.push(variant.id);
              }

              if (
                variant.appointment_prices &&
                variant.appointment_prices.length > 0
              ) {
                for (
                  let api = 0;
                  api < variant.appointment_prices.length;
                  api += 1
                ) {
                  const aptPrice = variant.appointment_prices[api];
                  aptPrice.appoint_price = convertBathToStangAndRoundTwoDecimal(
                    aptPrice.appoint_price
                  );
                }
              }

              // Same stale-discount rule as the service level above.
              if (variant.is_out_of_stock && variant.is_discount) {
                variant.is_discount = false;

                if (existValueAndEmptyString(variant.original_price)) {
                  variant.price = variant.original_price;
                  distributePrice(
                    variant.appointment_prices,
                    variant.original_price
                  );
                }
              }
            }
          }
        }
      }

      return packageOptionsParam;
    },
    async setupPackageOptionsMultiplyPrice(packageOptionsDetail) {
      if (!packageOptionsDetail) return undefined;

      const packageOptions = packageOptionsDetail.map((packageOption) => {
        let serviceOptionVariants;
        let serviceAppointmentPrices;

        const servicePrice = convertStangToBathAndRoundTwoDecimal(
          packageOption.service_price
        );
        const serviceOriginalPrice = convertStangToBathAndRoundTwoDecimal(
          packageOption.service_original_price
        );

        if (
          packageOption.service_appointment_prices &&
          packageOption.service_appointment_prices.length > 0
        ) {
          serviceAppointmentPrices =
            packageOption.service_appointment_prices.map(
              (serviceAppointmentPrice) => {
                const appointPrice = convertStangToBathAndRoundTwoDecimal(
                  serviceAppointmentPrice.appoint_price
                );

                return {
                  appoint_no: serviceAppointmentPrice.appoint_no,
                  appoint_price: appointPrice,
                };
              }
            );
        }

        if (packageOption.service_option_variants) {
          serviceOptionVariants = packageOption.service_option_variants.map(
            (optionVariant) => {
              const variants = optionVariant.variants.map((variant) => {
                const originalPrice = convertStangToBathAndRoundTwoDecimal(
                  variant.original_price
                );
                const price = convertStangToBathAndRoundTwoDecimal(
                  variant.price
                );

                const isEmptyIsNotSaleNewOption = this.newOptionVariantPrices
                  ? this.newOptionVariantPrices.some(
                      (newOptionVariant) => newOptionVariant === variant.id
                    )
                  : false;

                const isNotSale =
                  price >= 0 && isEmptyIsNotSaleNewOption
                    ? false
                    : variant.is_not_sale;

                const appointmentPrices =
                  variant.appointment_prices &&
                  variant.appointment_prices.length > 0
                    ? variant.appointment_prices.map((appointmentPrice) => {
                        const appointPrice =
                          convertStangToBathAndRoundTwoDecimal(
                            appointmentPrice.appoint_price
                          );

                        return {
                          appoint_no: appointmentPrice.appoint_no,
                          appoint_price: appointPrice,
                        };
                      })
                    : null;

                return {
                  extra_option: variant.extra_option,
                  id: variant.id,
                  is_discount: variant.is_discount,
                  is_not_sale: isNotSale,
                  is_out_of_stock: variant.is_out_of_stock,
                  original_price: originalPrice,
                  price,
                  title: variant.title,
                  appointment_prices: appointmentPrices,
                };
              });

              return {
                id: optionVariant.id,
                title: optionVariant.title,
                variants,
              };
            }
          );
        }

        return {
          is_discount: packageOption.is_discount,
          is_not_sale: packageOption.is_not_sale,
          is_out_of_stock: packageOption.is_out_of_stock,
          service_extra_option: packageOption.service_extra_option,
          service_id: +packageOption.service_id,
          service_main_option: packageOption.service_main_option,
          service_option_variants: serviceOptionVariants,
          service_original_price: serviceOriginalPrice,
          service_price: servicePrice,
          service_title: packageOption.service_title,
          service_appointment_prices: serviceAppointmentPrices,
          is_appointment: packageOption.is_appointment,
        };
      });

      return packageOptions;
    },
    async resolveWatermarkUrl(input, name) {
      if (input.watermarkFile) {
        return uploadPublicImage(input.watermarkFile, name, "article");
      }
      if (input.watermarkUrl) {
        return input.watermarkUrl;
      }
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: $space 0;
  max-width: 100%;
}
</style>
